import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import { useCreateExpenseMutation } from "@/redux/services/expense/expense.api";
import { ExpensePaymentMethod } from "@/redux/services/expense/expense.type";
import { EXPENSE_PAYMENT_METHODS } from "@/lib/expenses";
import { hasApiErrorKey, toastApiError } from "@/lib/apiErrors";

setLocale(fr);

export interface ExpenseFormData {
  category: string; // IRI
  amount: number; // in currency units (converted to cents on submit)
  paymentMethod: ExpensePaymentMethod;
  paidFromDrawer?: boolean;
  incurredAt: string; // YYYY-MM-DD
  payee?: string;
  description?: string;
}

// The API blocks a from-drawer expense when no cash session is open.
const NO_OPEN_SESSION = "expense.drawer.no_open_session";

const methodValues = EXPENSE_PAYMENT_METHODS.map((m) => m.value);

export const expenseValidationSchema: yup.ObjectSchema<ExpenseFormData> = yup
  .object()
  .shape({
    category: yup.string().required().label("Catégorie"),
    amount: yup
      .number()
      .transform((value, original) =>
        original === "" || original === null || original === undefined
          ? undefined
          : value
      )
      .typeError("Le montant doit être un nombre")
      .required()
      .positive("Le montant doit être supérieur à zéro")
      .label("Montant"),
    paymentMethod: yup
      .mixed<ExpensePaymentMethod>()
      .oneOf(methodValues)
      .required()
      .label("Moyen de paiement"),
    paidFromDrawer: yup.boolean().optional(),
    incurredAt: yup.string().required().label("Date"),
    payee: yup.string().optional().label("Bénéficiaire"),
    description: yup.string().optional().max(1000).label("Description"),
  });

const today = () => new Date().toISOString().slice(0, 10);

export const useCrudExpense = (
  closeModal: () => void,
  restaurantId: string
) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
  } = useForm<ExpenseFormData>({
    resolver: yupResolver(expenseValidationSchema),
    defaultValues: {
      category: "",
      amount: undefined,
      paymentMethod: undefined,
      incurredAt: today(),
      payee: "",
      description: "",
    },
  });

  const [createExpense, { isLoading }] = useCreateExpenseMutation();
  const [noOpenSession, setNoOpenSession] = useState(false);

  const onSubmit = async (data: ExpenseFormData) => {
    setNoOpenSession(false);
    const res = await createExpense({
      restaurantId,
      data: {
        category: data.category,
        amount: Math.round(data.amount * 100),
        paymentMethod: data.paymentMethod,
        paidFromDrawer: data.paidFromDrawer ?? false,
        incurredAt: data.incurredAt,
        payee: data.payee?.trim() || null,
        description: data.description?.trim() || null,
      },
    });

    if ("data" in res) {
      toast.success("Dépense enregistrée avec succès");
      reset({
        category: "",
        amount: undefined,
        paymentMethod: undefined,
        paidFromDrawer: false,
        incurredAt: today(),
        payee: "",
        description: "",
      });
      closeModal();
    } else if ("error" in res) {
      // No open cash session → surface an inline CTA instead of a toast.
      if (hasApiErrorKey(res.error, NO_OPEN_SESSION)) {
        setNoOpenSession(true);
        return;
      }
      toastApiError(res.error);
    }
  };

  return {
    register,
    control,
    watch,
    setValue,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
    noOpenSession,
  };
};
